import React, { useState, useEffect } from 'react';
import { MessageSquare, Pin, Trash2, Send, ThumbsUp, ChevronDown, X } from 'lucide-react';
import { chatAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

const MODERATOR_ROLES = ['organiser', 'super_admin', 'finance_admin', 'translator_admin', 'support_admin'];

function ReplyRow({ reply, onEdited, onDeleted, currentUserId, canModerate }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(reply.body);
  const [liked, setLiked] = useState(!!reply.liked_by_me);
  const [likes, setLikes] = useState(reply.likes || 0);
  const canManage = currentUserId === reply.user_id;

  const like = async () => {
    try {
      const r = await chatAPI.likeReply(reply.id);
      setLiked(r.data.liked);
      setLikes(n => r.data.liked ? n + 1 : Math.max(0, n - 1));
    } catch { toast.error('Failed to like'); }
  };

  const save = async () => {
    if (!text.trim()) return;
    try {
      const r = await chatAPI.editReply(reply.id, { body: text.trim() });
      onEdited(r.data);
      setEditing(false);
    } catch { toast.error('Failed to edit'); }
  };

  const del = async () => {
    if (!window.confirm('Delete this reply?')) return;
    try {
      await chatAPI.deleteReply(reply.id);
      onDeleted(reply.id);
    } catch { toast.error('Failed to delete'); }
  };

  return (
    <div className="flex gap-2 py-2" data-testid={`reply-${reply.id}`}>
      <div className="w-7 h-7 rounded-full bg-gray-800 flex items-center justify-center text-xs font-bold text-white flex-shrink-0 overflow-hidden">
        {(reply.users?.name || 'A')[0].toUpperCase()}
      </div>
      <div className="flex-1">
        <div className="flex items-center gap-2 mb-0.5">
          <span className="text-white text-xs font-semibold">{reply.users?.name || 'User'}</span>
          <span className="text-gray-600 text-xs">{reply.created_at?.slice(0, 10)}</span>
        </div>
        {editing ? (
          <div className="flex gap-2 mt-1">
            <input value={text} onChange={e => setText(e.target.value)} className="flex-1 bg-[#111] border border-gray-700 text-white text-xs px-2 py-1.5 rounded" />
            <button onClick={save} className="text-xs text-red-400 font-bold">Save</button>
            <button onClick={() => setEditing(false)} className="text-xs text-gray-500">Cancel</button>
          </div>
        ) : (
          <p className="text-gray-400 text-xs leading-relaxed">{reply.body}</p>
        )}
        <div className="flex items-center gap-3 mt-1">
          <button onClick={like} className={`flex items-center gap-1 text-xs transition-colors ${liked ? 'text-red-400' : 'text-gray-600 hover:text-gray-400'}`}>
            <ThumbsUp size={11} fill={liked ? 'currentColor' : 'none'} /> {likes || ''}
          </button>
          {(canManage || canModerate) && !editing && (
            <>
              {canManage && <button onClick={() => setEditing(true)} className="text-xs text-gray-600 hover:text-gray-400">Edit</button>}
              <button onClick={del} className="text-xs text-gray-600 hover:text-red-400">Delete</button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function TopicCard({ topic, onDeleted, canModerate, currentUserId }) {
  const [expanded, setExpanded] = useState(false);
  const [replies, setReplies] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [pinned, setPinned] = useState(!!topic.is_pinned);
  const { user } = useAuth();

  const loadReplies = async () => {
    try {
      const r = await chatAPI.getReplies(topic.id);
      setReplies(r.data.replies || []);
      setLoaded(true);
    } catch { toast.error('Failed to load replies'); }
  };

  const toggle = () => {
    setExpanded(v => !v);
    if (!loaded) loadReplies();
  };

  const submitReply = async () => {
    if (!replyText.trim()) return;
    try {
      const r = await chatAPI.addReply(topic.id, { body: replyText.trim() });
      setReplies(prev => [...prev, r.data]);
      setReplyText('');
    } catch { toast.error('Failed to reply'); }
  };

  const togglePin = async () => {
    try {
      await chatAPI.pinTopic(topic.id, { is_pinned: !pinned });
      setPinned(v => !v);
      toast.success(!pinned ? 'Topic pinned' : 'Topic unpinned');
    } catch { toast.error('Failed to update pin'); }
  };

  const del = async () => {
    if (!window.confirm('Delete this topic and all its replies?')) return;
    try {
      await chatAPI.deleteTopic(topic.id);
      onDeleted(topic.id);
      toast.success('Topic deleted');
    } catch { toast.error('Failed to delete'); }
  };

  return (
    <div className="bg-[#0D0D0D] border border-gray-800/80 rounded-xl p-5" data-testid={`topic-${topic.id}`}>
      <div className="flex items-start justify-between gap-3 mb-2">
        <button onClick={toggle} className="flex-1 text-left">
          <div className="flex items-center gap-2 mb-1">
            {pinned && <Pin size={12} className="text-yellow-400" fill="currentColor" />}
            <h4 className="text-white font-bold text-sm">{topic.title}</h4>
          </div>
          <p className="text-gray-500 text-xs">by {topic.users?.name || 'User'} · {topic.created_at?.slice(0, 10)}</p>
        </button>
        <div className="flex items-center gap-2 flex-shrink-0">
          {canModerate && (
            <button onClick={togglePin} className={`text-xs ${pinned ? 'text-yellow-400' : 'text-gray-600 hover:text-gray-400'}`}>
              <Pin size={13} fill={pinned ? 'currentColor' : 'none'} />
            </button>
          )}
          {(canModerate || currentUserId === topic.user_id) && (
            <button onClick={del} className="text-gray-600 hover:text-red-400"><Trash2 size={13} /></button>
          )}
        </div>
      </div>

      {topic.body && <p className="text-gray-300 text-sm mb-2">{topic.body}</p>}

      <button onClick={toggle} className="flex items-center gap-1.5 text-gray-500 hover:text-gray-300 text-xs font-medium transition-colors">
        <MessageSquare size={12} />
        {topic.reply_count ?? replies.length} {(topic.reply_count ?? replies.length) === 1 ? 'reply' : 'replies'}
        <ChevronDown size={11} className={`transition-transform ${expanded ? 'rotate-180' : ''}`} />
      </button>

      {expanded && (
        <div className="mt-3 pl-4 border-l border-gray-800">
          {replies.map(r => (
            <ReplyRow
              key={r.id}
              reply={r}
              currentUserId={user?.user_id}
              canModerate={canModerate}
              onEdited={upd => setReplies(prev => prev.map(x => x.id === upd.id ? upd : x))}
              onDeleted={id => setReplies(prev => prev.filter(x => x.id !== id))}
            />
          ))}
          {user && (
            <div className="flex gap-2 mt-2">
              <input
                value={replyText}
                onChange={e => setReplyText(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && submitReply()}
                placeholder="Write a reply..."
                className="flex-1 bg-[#111] border border-gray-700 text-white text-xs px-3 py-2 rounded focus:border-red-500 outline-none"
              />
              <button onClick={submitReply} disabled={!replyText.trim()} className="px-3 py-2 btn-primary rounded text-xs font-bold disabled:opacity-40">
                <Send size={12} />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function DiscussionsSection({ eventId }) {
  const { user } = useAuth();
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const canModerate = user && (MODERATOR_ROLES.includes(user.role) || user.is_dj);

  const load = async () => {
    try {
      const r = await chatAPI.getTopics(eventId);
      setTopics(r.data.topics || []);
    } catch { /* ignore */ } finally { setLoading(false); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [eventId]);

  const submit = async () => {
    if (!title.trim()) { toast.error('Please add a title'); return; }
    setSubmitting(true);
    try {
      const r = await chatAPI.createTopic(eventId, { title: title.trim(), body: body.trim() });
      setTopics(prev => [r.data, ...prev]);
      setTitle(''); setBody(''); setShowForm(false);
      toast.success('Discussion started!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create topic');
    } finally { setSubmitting(false); }
  };

  return (
    <div className="mt-8" data-testid="discussions-section">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-black uppercase text-white tracking-wide" style={{ fontFamily: 'Montserrat, sans-serif' }}>
            Discussions
          </h2>
          <p className="text-gray-500 text-xs mt-1">{topics.length} {topics.length === 1 ? 'topic' : 'topics'}</p>
        </div>
        {user && !showForm && (
          <button onClick={() => setShowForm(true)} className="btn-primary text-xs px-4 py-2 rounded font-bold flex items-center gap-1.5" data-testid="new-topic-btn">
            <MessageSquare size={12} /> New Topic
          </button>
        )}
      </div>

      {showForm && (
        <div className="bg-[#0D0D0D] border border-gray-700 rounded-xl p-5 mb-6" data-testid="topic-form">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-white font-bold text-sm">Start a Discussion</h3>
            <button onClick={() => setShowForm(false)} className="text-gray-500 hover:text-white"><X size={16} /></button>
          </div>
          <input
            value={title}
            onChange={e => setTitle(e.target.value)}
            placeholder="Topic title"
            className="w-full bg-[#111] border border-gray-700 text-white text-sm px-3 py-2.5 rounded mb-3 focus:border-red-500 outline-none"
          />
          <textarea
            value={body}
            onChange={e => setBody(e.target.value)}
            placeholder="What's on your mind? (optional)"
            rows={3}
            className="w-full bg-[#111] border border-gray-700 text-white text-sm px-3 py-2.5 rounded mb-4 resize-none focus:border-red-500 outline-none"
          />
          <div className="flex gap-3">
            <button onClick={() => setShowForm(false)} className="flex-1 py-2.5 border border-gray-700 rounded text-sm text-gray-400 hover:text-white transition-colors">Cancel</button>
            <button onClick={submit} disabled={submitting || !title.trim()} className="flex-1 btn-primary py-2.5 rounded text-sm font-bold disabled:opacity-40 flex items-center justify-center gap-2">
              <Send size={14} /> Post Topic
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="space-y-4">{[1, 2].map(i => <div key={i} className="skeleton h-24 rounded-xl" />)}</div>
      ) : topics.length === 0 ? (
        <div className="text-center py-12 border border-dashed border-gray-800 rounded-xl">
          <MessageSquare className="w-10 h-10 text-gray-700 mx-auto mb-3" />
          <p className="text-gray-500 text-sm">No discussions yet. Be the first to start one!</p>
        </div>
      ) : (
        <div className="space-y-4">
          {topics.map(topic => (
            <TopicCard
              key={topic.id}
              topic={topic}
              canModerate={canModerate}
              currentUserId={user?.user_id}
              onDeleted={id => setTopics(prev => prev.filter(t2 => t2.id !== id))}
            />
          ))}
        </div>
      )}
    </div>
  );
}
